import dayjs from "dayjs";
import { WIPCampaignMetrics } from "../../../store/apis/wipApi";
import type { ManualMetricField } from "./columns";

/** Safe division that returns 0 when denominator is 0 or non-finite. */
function safeDiv(numerator: number, denominator: number): number {
  if (denominator === 0 || !Number.isFinite(denominator)) return 0;
  const result = numerator / denominator;
  return Number.isFinite(result) ? result : 0;
}

/**
 * Derived fields computed from WIPCampaignMetrics.
 * These are pre-computed before rendering so that both cell rendering
 * and CSV export share the same formula without duplication.
 */
export interface DerivedFields {
  /** expectedVolumeUntilYesterday / targetVolume (0–1) */
  expectedVolumePercent: number;
  /** accumulatedVolumeUntilYesterday / targetVolume (0–1) */
  obtainedVolumePercent: number;
  /** accumulatedVolumeUntilYesterday − expectedVolumeUntilYesterday */
  volumeVariation: number;
  /** volumeVariation / expectedVolumeUntilYesterday (signed ratio) */
  volumeVariationPercent: number;
  /** Inversión plataforma que deja el margen ideal: totalInversion × (1 − margen)
   *  − costes añadidos.
   *  Sin tocar coincide con investmentNeededTotalReal; con un borrador, es la
   *  que se enviaría al aceptar. null sin margen ideal. */
  idealInvestment: number | null;
  /** viewableImpressions / measurableImpressions (0–1). null sin medibles. */
  viewabilityRate: number | null;
  /** engagements / impressions (0–1). null sin impresiones o sin interacciones. */
  engagementRate: number | null;
  /** Costes unitarios reales sobre el coste de cliente acumulado, sea cual sea
   *  el objetivo de la línea. null sin unidades de esa métrica. */
  cpmReal: number | null;
  cpcReal: number | null;
  cpvReal: number | null;
  cpaReal: number | null;
  cpeReal: number | null;
}

/** Ratio that stays null (→ "—") when there is nothing to divide by. */
const ratioOrNull = (numerator: number, denominator: number): number | null =>
  denominator > 0 && Number.isFinite(numerator)
    ? numerator / denominator
    : null;

export type EnrichedRow = WIPCampaignMetrics & DerivedFields;

export function enrichRow(r: WIPCampaignMetrics): EnrichedRow {
  const volumeVariation =
    r.accumulatedVolumeUntilYesterday - r.expectedVolumeUntilYesterday;
  return {
    ...r,
    expectedVolumePercent: safeDiv(
      r.expectedVolumeUntilYesterday,
      r.targetVolume
    ),
    obtainedVolumePercent: safeDiv(
      r.accumulatedVolumeUntilYesterday,
      r.targetVolume
    ),
    volumeVariation,
    volumeVariationPercent: safeDiv(
      volumeVariation,
      r.expectedVolumeUntilYesterday
    ),
    idealInvestment:
      r.marginIdeal == null ? null : investmentForMargin(r, r.marginIdeal),
    viewabilityRate: ratioOrNull(
      r.viewableImpressions,
      r.measurableImpressions
    ),
    engagementRate:
      r.engagements > 0 ? ratioOrNull(r.engagements, r.impressions) : null,
    cpmReal: ratioOrNull(r.accumulatedClientCost * 1000, r.impressions),
    cpcReal: ratioOrNull(r.accumulatedClientCost, r.clicks),
    cpvReal: ratioOrNull(r.accumulatedClientCost, r.completeViews),
    cpaReal: ratioOrNull(r.accumulatedClientCost, r.conversions),
    cpeReal: ratioOrNull(r.accumulatedClientCost, r.engagements),
  };
}

/** A margin draft is valid when it is a 0–100 number ("12,5" works too). */
export function parseMarginDraft(draft: string): number | null {
  const n = parseFloat(draft.replace(",", "."));
  return Number.isFinite(n) && n >= 0 && n <= 100 ? n : null;
}

/** True when the row's margin can be changed from tomorrow on (from today on its last day). */
export const isMarginEditable = (r: WIPCampaignMetrics): boolean =>
  r.marginForecast?.fromDate != null;

/** When a margin accepted now applies: "desde hoy" (last day), "desde mañana" or a date. */
export function marginFromText(
  r: Pick<WIPCampaignMetrics, "marginForecast">
): string {
  const from = r.marginForecast?.fromDate;
  if (!from) return "";
  if (from === dayjs().format("YYYY-MM-DD")) return "desde hoy";
  if (from === dayjs().add(1, "day").format("YYYY-MM-DD"))
    return "desde mañana";
  return `desde el ${dayjs(from).format("DD/MM/YYYY")}`;
}

/** Platform investment a margin leaves on the client investment once the
 *  added costs (adserver, trackers…) are out, like the brief's margin. */
export const investmentForMargin = (
  r: Pick<WIPCampaignMetrics, "totalInversion" | "addedCostsTotal">,
  margin: number
): number => r.totalInversion * (1 - margin / 100) - r.addedCostsTotal;

/** The row with an edited ideal margin: the investment to accept follows it. */
export function applyMarginDraft(
  row: EnrichedRow,
  margin: number
): EnrichedRow {
  return {
    ...row,
    marginIdeal: margin,
    idealInvestment: investmentForMargin(row, margin),
  };
}

/** Mensaje de error que devuelve el backend en una petición fallida, si lo hay. */
export const getErrorMessage = (error: unknown): string | null => {
  if (typeof error === "object" && error !== null && "data" in error) {
    const data = (error as { data?: { message?: string } }).data;
    if (data && typeof data.message === "string") return data.message;
  }
  return null;
};

/**
 * Parses a manual metric draft. Empty clears the value (null = "sin dato");
 * counts must be whole numbers, the investment may carry decimals ("1.234,5"
 * and "1234.5" both work). undefined = not a valid value.
 */
export function parseManualDraft(
  field: ManualMetricField,
  draft: string
): number | null | undefined {
  const trimmed = draft.trim();
  if (trimmed === "") return null;
  // "1.234,56" → "1234.56"; a lone comma is a decimal separator.
  const normalized = trimmed.includes(",")
    ? trimmed.replace(/\./g, "").replace(",", ".")
    : trimmed;
  const n = Number(normalized);
  if (!Number.isFinite(n) || n < 0) return undefined;
  if (field !== "platformInversion" && !Number.isInteger(n)) return undefined;
  return n;
}
